import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import ChartDataLabels from "chartjs-plugin-datalabels";

import { Card, Skeleton, Box } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import ChartFilter from "./Filter";
import OrderFilter from "./OrderFilter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const AveragePeopleChart = () => {
  const theme = useTheme();

  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);

  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);

  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  // -----------------------------
  // Fetch Data
  // -----------------------------
  const fetchData = async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        `${baseURL}analytics/cameras/average-people`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: filter,
        }
      );

      setDataList(data.data || []);
    } catch (error) {
      console.error("Error fetching frames:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  // -----------------------------
  // Chart Data
  // -----------------------------
  const labels = dataList.map((item) => item.camera_name);
  const values = dataList.map((item) => item.avg_person_count);

  // 🎨 ألوان من الـ Theme
  const barColor = alpha(theme.palette.info.main, 0.85);
  const textColor = theme.palette.text.primary;
  const gridColor = theme.palette.divider;

  const data = {
    labels,
    datasets: [
      {
        label: "Average People Count",
        data: values,
        backgroundColor: barColor,
        borderRadius: 6,
      },
    ],
  };

  // -----------------------------
  // Chart Options (Theme-based)
  // -----------------------------
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          color: textColor,
        },
      },
      title: {
        display: true,
        text: "Average People per Camera",
        color: textColor,
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1,
      },
      // ✅ أرقام القيم جوّه الأعمدة
      datalabels: {
        color: theme.palette.getContrastText(
          theme.palette.info.main
        ),
        anchor: "center",
        align: "center",
        font: {
          weight: "bold",
          size: 13,
        },
        formatter: (val) => {
          if (val === null || val === 0) return "";
          return val.toFixed(2);
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
        title: { display: true, text: "Camera Name", color: textColor },
      },

      y: {
        beginAtZero: true,
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
        title: { display: true, text: "People Count", color: textColor },
      },
    },
  };

  // -----------------------------
  // Filter Popup
  // -----------------------------
  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={setFilter}
        />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  return (
    <Card sx={{ p: 2 }}>
      <DesBtn text="Filter" handle={openFilter}>
        <FilterAltOutlinedIcon />
      </DesBtn>

      {loading ? (
        <Skeleton
          variant="rectangular"
          height="500px"
          sx={{ borderRadius: 2 }}
        />
      ) : (
        <Box sx={{ height: "500px" }}>
          <Bar data={data} options={options} />
        </Box>
      )}
    </Card>
  );
};

export default AveragePeopleChart;
